<template>
  <div class="subscription-root">
    <QHeader class="bg-transparent text-white">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="cancelOrGoBack" />
      </QToolbar>
    </QHeader>
    <QPageContainer>
      <QPage>
        <section v-if="!shouldShowIAP">
          <component :is="pagesToDisplay" :demand-id="demandId" @cancel-or-go-back="cancelOrGoBack" @give-feedback="giveFeedback" />
        </section>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { mapState } from "pinia";
  import { useProfileStore } from "@etab/stores/profile";
  import { GETTER_PROFILE_GET_SUBSCRIPTION_STATUS, ACTION_PROFILE_GET_SUBSCRIPTION_STATUS, GETTER_PROFILE_GET } from "@etab/stores/types";
  import { PurchaselyManager } from "@etab/managers/purchasely.manager";
  import { ETAB_SUBSCRIPTION_STATUS, META_BACK_ROUTE, PURCHASELY_DEMAND_PLACEMENT_ID } from "@core/constants";
  import { ETAB_ROUTES } from "@etab/config";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { EtabSubscriptionService } from "@core/services";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { JavascriptHelpers, RouterHelpers, SanitationHelper } from "@core/helpers";

  import SubscriptionOfferPage from "./SubscriptionOffers.page.vue";
  import SubscriptionPage from "./Subscription.page.vue";

  export default {
    computed: {
      ...mapState(useProfileStore, {
        etabProfile: GETTER_PROFILE_GET,
        subscriptionStatus: GETTER_PROFILE_GET_SUBSCRIPTION_STATUS,
      }),
      canSubscribe() {
        return this.subscriptionStatus === ETAB_SUBSCRIPTION_STATUS.CAN_SUBSCRIBE;
      },
      hasActiveSubscription() {
        return this.subscriptionStatus === ETAB_SUBSCRIPTION_STATUS.ACTIVATED;
      },
      demandId() {
        return this.$route.params.demandId;
      },
      shouldShowIAP() {
        return PurchaselyManager.isIAPActivated() && this.canSubscribe;
      },
      pagesToDisplay() {
        if (this.canSubscribe && !this.shouldShowIAP) {
          return SubscriptionOfferPage;
        }
        return SubscriptionPage;
      },
      backRoute() {
        return RouterHelpers.getRouteMeta(this.$route, META_BACK_ROUTE) || undefined;
      },
    },
    created() {
      if (this.shouldShowIAP) {
        this.showIAP();
      }
    },
    methods: {
      showIAP() {
        PurchaselyManager.showPayWall({
          placementId: PURCHASELY_DEMAND_PLACEMENT_ID,
          contendId: this.demandId,
          successCallback: async (callback) => {
            switch (callback.result) {
              case PurchaselyManager.Purchasely.PurchaseResult.PURCHASED:
                try {
                  this.$_startGlobalLoading();
                  await this.refreshUserSubscriptionStatus();
                } catch (error) {
                  NotificationsManager.error();
                } finally {
                  this.$_stopGlobalLoading();
                  this.goToDashboard();
                }
                break;
              case PurchaselyManager.Purchasely.PurchaseResult.RESTORED:
                await this.refreshUserSubscriptionStatus();
                this.goToDashboard();
                break;
              case PurchaselyManager.Purchasely.PurchaseResult.CANCELLED:
                await this.giveFeedback({ isInterested: false, notifyUser: false });
                this.goToDashboard();
                break;
              default:
                this.goToDashboard();
                break;
            }
          },
          errorCallback: (error) => {
            LoadingManager.error();
            this.goToDashboard();
          },
        });
      },
      goToDashboard() {
        return this.$router.push({ name: ETAB_ROUTES.dashboard.tabs.upcoming.name }).catch((suppressedError) => {});
      },
      cancelOrGoBack() {
        if (this.demandId) {
          return this.$router.push({ name: ETAB_ROUTES.upfront.name, params: { demandId: this.demandId } }).catch((suppressedError) => {});
        }
        if (this.backRoute) {
          return this.$router.push({ name: this.backRoute }).catch((suppressedError) => {});
        }
        return this.$router.go(-1);
      },
      async refreshUserSubscriptionStatus() {
        const profileStore = useProfileStore();
        return profileStore[ACTION_PROFILE_GET_SUBSCRIPTION_STATUS]();
      },
      async giveFeedback({ isInterested, highlightId, offerId, notifyUser }) {
        try {
          this.$_startGlobalLoading();
          const data = await EtabSubscriptionService.giveFeedback({ highlightId, isInterested, offerId });
          if (notifyUser) {
            this.notifyUser({ message: data });
          }
        } catch (error) {
          const message = error.data;
          NotificationsManager.error({ message });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      notifyUser({ message } = {}) {
        const sanitizedMessage = SanitationHelper.sanitize(JavascriptHelpers.ensureStringOrEmpty(message));

        return NotificationsManager.info({
          message: sanitizedMessage,
          options: {
            html: true,
            timeout: 0,
            closeBtn: false,
            actions: [
              {
                icon: "icon-f-check",
                color: "white",
                handler: () => {
                  // close
                },
              },
            ],
          },
        });
      },
    },
  };
</script>
